import React, { useEffect, useState } from "react";
import { useHistory } from "react-router-dom";
import axios, { CancelToken } from "axios";

import Page from "components/page/Page";
import Card from "components/card/Card";
import FormLayout from "components/form/FormLayout";
import Input from "components/form/input/Input";
import { Select } from "components/select/index";
import { StatusCode } from "api/protocols";

import { useTranslation } from "react-i18next";
import { PostTaxLine } from "api/rpc/2024-04/facilityAdmin/client/taxLines";
import { GetAccountingReference } from "api/rpc/2024-04/facilityAdmin/facility/accounting";
import { TAccountingReference } from "redux/reducers/models/facility";
import Toggle from "components/form/toggle/Toggle";
import {
  accountingReferenceMatchesQuery,
  formatAccountingReferenceLabel,
} from "utils/formatAccountingReferenceLabel";

interface ITaxLineState {
  name: string;
  percentage: number;
  accountingReferences: Array<TAccountingReference>;
  accounting_reference_id: number;
  accountingRefSearch: string;
  default: boolean;
  tax_exempt: boolean;
}

export default function TaxLinesNew() {
  const history = useHistory();
  const { t } = useTranslation();
  const [taxLineState, setTaxLineState] = useState<ITaxLineState>({
    name: "",
    percentage: null,
    accountingReferences: [],
    accounting_reference_id: null,
    accountingRefSearch: "",
    default: false,
    tax_exempt: false,
  });

  useEffect(() => {
    const source = axios.CancelToken.source();
    void loadAccountingReferences(source.token);
  }, []);

  async function loadAccountingReferences(token: CancelToken) {
    const res = await GetAccountingReference(null, false, token);

    if (token && token.reason) {
      return;
    }
    if (res.status !== StatusCode.OK) {
      return;
    }

    const normalizedReferences: TAccountingReference[] = [
      { id: -1, client_id: null as number, title: t("None"), reference_number: null as string },
      ...res.data,
    ];

    setTaxLineState(prev => ({ ...prev, accountingReferences: normalizedReferences }));
  }

  function handleInputChange(event: any) {
    const { id, value } = event.target;
    setTaxLineState(prevState => ({ ...prevState, [id]: value }));
  }

  async function saveNewTaxLine() {
    console.log(taxLineState);
    const res = await PostTaxLine(
      {
        name: taxLineState.name,
        percentage: taxLineState.percentage,
        accounting_reference_id: taxLineState.accounting_reference_id,
        default: taxLineState?.default,
        tax_exempt: taxLineState.tax_exempt,
      },
      true,
    );
    if (res.status !== StatusCode.OK) {
      return;
    }

    console.log(res);
    history.push("/admin/settings/taxes");
  }

  const primaryAction = {
    content: t("Save"),
    action: saveNewTaxLine,
  };

  return (
    <Page
      title={t("New Tax Line")}
      narrow
      primaryAction={primaryAction}
      breadcrumbs={[
        {
          prefix: true,
          label: t("Tax Lines"),
          url: "/admin/settings/taxes",
        },
      ]}
    >
      <Card>
        <Card.Section>
          <FormLayout>
            <FormLayout.Group>
              <Input
                value={taxLineState.name}
                label={t("Name")}
                id="name"
                onChange={handleInputChange}
                placeholder={t("Name")}
              />
            </FormLayout.Group>
            <FormLayout.Group>
              <Input
                value={taxLineState.percentage}
                label={t("Percentage")}
                id="percentage"
                onChange={handleInputChange}
                placeholder={t("Percentage")}
              />
              <Select
                label={t("Accounting Reference")}
                showSearch
                onSearch={(query: string) => setTaxLineState(prev => ({ ...prev, accountingRefSearch: query }))}
                onChange={(value: number) => setTaxLineState(prev => ({ ...prev, accounting_reference_id: value }))}
                allowClear
                placeholder={t("secure.facility.product.product_new.031")}
                searchValue={taxLineState.accountingRefSearch}
                showDropDownOnFocus={true}
              >
                {taxLineState.accountingReferences
                  .filter(accountRef =>
                    accountingReferenceMatchesQuery(
                      accountRef.title ?? "",
                      accountRef.reference_number,
                      taxLineState.accountingRefSearch,
                    ),
                  )
                  .map(reference => {
                    const label = formatAccountingReferenceLabel(
                      reference.title ?? "",
                      reference.reference_number,
                    );
                    return (
                      <div key={reference.id}>
                        {taxLineState.accountingRefSearch !== "" ? (
                          <div>
                            <Select.Option key={reference.id} value={reference.id} name={label}>
                              <p className="font-semibold text-lg">{label}</p>
                            </Select.Option>
                          </div>
                        ) : null}
                      </div>
                    );
                  })}
              </Select>
            </FormLayout.Group>
            <FormLayout.Group>
              <Toggle
                label={t("Default")}
                checked={taxLineState?.default}
                id="default"
                onChange={e => setTaxLineState(prev => ({ ...prev, [e.target.id]: e.target.checked }))}
                labelSupport={t("Enable to automatically apply this tax to new products")}
              />
              <Toggle
                label={t("Tax Exempt")}
                checked={taxLineState.tax_exempt}
                id="tax_exempt"
                onChange={event =>
                  setTaxLineState(prev => ({ ...prev, [event.target.id]: event.target.checked }))
                }
                labelSupport={t("Enable to allow this tax line to be exempted on eligible transactions")}
              />
            </FormLayout.Group>
          </FormLayout>
        </Card.Section>
      </Card>
    </Page>
  );
}
